import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Environment } from '@rma/dashboard/generic/util/environment';
import { BaseConnectLinkedAccountService, LinkedAccountsPlatformType } from '@rma/dashboard/linked-accounts/private/domain';
import { appendToRmaReviewsApiUrl } from '@rma/generic/ng/util';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { ConnectionStatus, ConnectToZillowSetting } from './connect-to-zillow-setting.model';

@Injectable({
  providedIn: 'root',
})
export class ConnectToZillowService {
  private readonly platform = LinkedAccountsPlatformType[LinkedAccountsPlatformType.Zillow];

  private readonly httpClient = inject(HttpClient);
  private readonly reviewsApiUrl = inject(appendToRmaReviewsApiUrl);
  private readonly env = inject(Environment);
  private readonly linkedAccountsService = inject(BaseConnectLinkedAccountService);

  public getConnectToZillowSettings(): Observable<ConnectToZillowSetting | null> {
    return this.linkedAccountsService.getPlatformLinkedAccountSetting(this.platform).pipe(
      switchMap((result) => {
        return !result.PlatformData || !result.PlatformData['ScreenName']
          ? this.checkIfExternalIdentifierExists()
          : // SAFETY(legacy): unreviewed, pre-dates this rule.
            of(this.newSettings((result?.PlatformData['ScreenName'] as string) ?? '', result?.Enabled ?? true));
      }),
    );
  }

  public checkIfExternalIdentifierExists(): Observable<ConnectToZillowSetting | null> {
    return this.linkedAccountsService.getMyExternalIdentifierForPlatform(this.platform).pipe(
      map((result) => this.newSettings(result[0] ?? '', false)),
      catchError((err) => (err.status === 404 ? of(this.newSettings('', false)) : of(null))),
    );
  }

  public validateZillowSettings(vanityKey: string): Observable<void> {
    const url = this.reviewsApiUrl(`ExternalReviewsConfig/Zillow/${vanityKey}`);
    return this.httpClient.get<void>(url);
  }

  public saveConnectToZillowSettings(screenName: string, enabled: boolean): Observable<ConnectToZillowSetting> {
    const request = {
      PlatformData: {
        ScreenName: screenName,
      },
      Enabled: enabled,
    };

    return (
      this.linkedAccountsService
        .saveLinkedAccountSetting(request, this.platform)
        // SAFETY(legacy): unreviewed, pre-dates this rule.
        .pipe(map((result) => this.newSettings(result.PlatformData['ScreenName'] as string, result.Enabled)))
    );
  }

  private newSettings(screenName: string, enabled: boolean): ConnectToZillowSetting {
    const connectionStatus = screenName && enabled ? ConnectionStatus.Connected : ConnectionStatus.Disconnected;
    const profileUrl = `${this.env.zillowUrl}/profile/${screenName.replace(/ /g, '-')}`;
    const profileDisplayUrl = profileUrl.replace('https://www.', '');

    return {
      screenName,
      enabled,
      connectionStatus,
      profileUrl,
      profileDisplayUrl,
    };
  }
}
